import { FC, useEffect } from 'react';

import useDC from '@eneco/dc/useDC';
import { I18nProvider, useTranslation } from '@eneco/i18n';
import { Box, Button, Form, Heading, Skeleton, Stack, Text } from '@eneco/sparky';
import { useTracking } from '@eneco/tracking';
import { DC_Domain_Models_Usages_ReadingType } from '@monorepo-types/dc';
import { useForm } from 'react-hook-form';

import { useMeterReadingsFlow } from '../state/MeterReadingsFlow.machine';
import { Steps } from '../tracking';

export const IntroductionStep: FC = () => {
  const { label } = useDC();

  return (
    <I18nProvider dictionary={locale => import(`./content/IntroductionStep.${label}.${locale}.json`)}>
      <IntroductionStepComponent />
    </I18nProvider>
  );
};

export const IntroductionStepComponent: FC = () => {
  const { trackFunnelStep } = useTracking();
  const { t, isLoading } = useTranslation();
  const { state, send } = useMeterReadingsFlow();
  const addressLine = state.context.addressLine;
  const readingType = state.context.reading?.readingType as Extract<
    DC_Domain_Models_Usages_ReadingType,
    'EOS' | 'TME' | 'PER' | 'ER'
  >;
  const { handleSubmit } = useForm();

  useEffect(() => {
    trackFunnelStep({
      step: Steps[`INTRODUCTION.${readingType}`],
      funnel: state.context.funnelName,
    });
  }, [readingType, state.context.funnelName, trackFunnelStep]);

  if (isLoading) return <Skeleton height={210} />;

  const copy = {
    PER: {
      title: t('yearNote.title'),
      content: t('yearNote.content'),
    },
    EOS: {
      title: t('closeDown.title'),
      content: t('closeDown.content'),
    },
    TME: {
      title: t('tme.title'),
      content: t('tme.content'),
    },
    ER: {
      title: t('er.title'),
      content: t('er.content'),
    },
  };

  const title = copy[readingType].title;
  const content = copy[readingType].content;

  const onSubmit = () => send({ type: 'NEXT' });

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap="6">
        <Stack gap="2">
          <Heading as="h1" size="M">
            {title}
          </Heading>
          <Text>{content}</Text>
        </Stack>
        <Stack gap="10">
          <Box padding="4" backgroundColor="backgroundPrimary">
            <Stack gap="1">
              <Text>{t('addressLabel')}</Text>
              <Text size="BodyL" weight="bold">
                {addressLine}
              </Text>
            </Stack>
          </Box>

          <Stack.Item>
            <Button type="submit" isLoading={isLoading}>
              {t('submitButtonText')}
            </Button>
          </Stack.Item>
        </Stack>
      </Stack>
    </Form>
  );
};
